import {
  CANVAS_SNAP_THRESHOLD,
  canvasSnapLineMode,
  domNodeId,
  elementStatus,
  elementType,
} from '../../../utils/constants';
import { adjustImageSizeToFitContainer, convertToOriginalScale, convertToRealScale, rotateText } from '../../logics';
import { borderColorList, getStartedColorList } from '../../../nextgen/constants';

import { changeTextPosition } from '../../actions';
import { isMobileOnly } from 'react-device-detect';
import { keepTextElementInSide } from '../../../elements/components/BaseElement/logics';

export const changeTextPos = (originPos, dispatch, canvasKey, element, draggingArea, textCoverRectRef) => {
  const { width, height, direction } = element.data;

  const adjustedElement = keepTextElementInSide(
    draggingArea,
    originPos,
    { width, height },
    direction,
    elementType.Text
  );
  dispatch(
    changeTextPosition({
      canvasKey,
      textId: element.ui.guid,
      x: Math.round(adjustedElement.x),
      y: Math.round(adjustedElement.y),
    })
  );

  //TextCoverRect should follow the adjusted element
  const elementData = Object.assign({}, element.data);
  elementData.x = Math.round(adjustedElement.x);
  elementData.y = Math.round(adjustedElement.y);
  const realRect = getTextRealRect(elementData);
  textCoverRectRef.current.x(realRect.x);
  textCoverRectRef.current.y(realRect.y);
};

export const batchOutlineBoxDragBound = (pos, batchOutlineBox, draggingArea, scale) => {
  pos = convertToOriginalScale(pos, scale);

  let newX = Math.max(pos.x, draggingArea.x);
  newX = Math.min(newX, draggingArea.x + draggingArea.width - batchOutlineBox.width());
  let newY = Math.max(pos.y, draggingArea.y);
  newY = Math.min(newY, draggingArea.y + draggingArea.height - batchOutlineBox.height());

  return convertToRealScale({ x: newX, y: newY }, scale);
};

export const coverRectDragBound = (pos, element, textList, draggingArea, allMargin, scale) => {
  const updateBatchDragBox = (imageData, batchDragBox) => {
    const realRect = imageData.ui.outlineRect;
    if (batchDragBox.x === Number.MAX_SAFE_INTEGER && batchDragBox.y === Number.MAX_SAFE_INTEGER) {
      batchDragBox.x = realRect.x;
      batchDragBox.y = realRect.y;
      batchDragBox.width = realRect.width;
      batchDragBox.height = realRect.height;
    }

    if (batchDragBox.x > realRect.x) {
      batchDragBox.width += batchDragBox.x - realRect.x;
      batchDragBox.x = realRect.x;
    }
    if (batchDragBox.x + batchDragBox.width < realRect.x + realRect.width) {
      batchDragBox.width += realRect.x + realRect.width - (batchDragBox.x + batchDragBox.width);
    }

    if (batchDragBox.y > realRect.y) {
      batchDragBox.height += batchDragBox.y - realRect.y;
      batchDragBox.y = realRect.y;
    }

    if (batchDragBox.y + batchDragBox.height < realRect.y + realRect.height) {
      batchDragBox.height += realRect.y + realRect.height - (batchDragBox.y + batchDragBox.height);
    }
  };

  const batchDragBox = { x: Number.MAX_SAFE_INTEGER, y: Number.MAX_SAFE_INTEGER, width: 0, height: 0 };
  textList.forEach((image) => {
    if (image.ui.status === elementStatus.BatchSelected) {
      updateBatchDragBox(image, batchDragBox);
    }
  });

  const currentElementRealRect = element.ui.outlineRect;

  const batchDraggingArea = {
    x: currentElementRealRect.x - batchDragBox.x,
    y: currentElementRealRect.y - batchDragBox.y,
    width: draggingArea.width - batchDragBox.width,
    height: draggingArea.height - batchDragBox.height,
  };

  pos = convertToOriginalScale(pos, scale);

  let newX = Math.max(pos.x, batchDraggingArea.x + allMargin.x);
  newX = Math.min(newX, batchDraggingArea.x + batchDraggingArea.width + allMargin.x);
  let newY = Math.max(pos.y, batchDraggingArea.y + allMargin.y);
  newY = Math.min(newY, batchDraggingArea.y + batchDraggingArea.height + allMargin.y);

  return convertToRealScale({ x: newX, y: newY }, scale);
};

export const coverRectDragBoundText = (pos, element, draggingArea, allMargin, scale) => {
  const realRect = element.ui.outlineRect;
  const batchDragBox = { x: Number.MAX_SAFE_INTEGER, y: Number.MAX_SAFE_INTEGER, width: 0, height: 0 };

  if (batchDragBox.x === Number.MAX_SAFE_INTEGER && batchDragBox.y === Number.MAX_SAFE_INTEGER) {
    batchDragBox.x = realRect.x;
    batchDragBox.y = realRect.y;
    batchDragBox.width = realRect.width;
    batchDragBox.height = realRect.height;
  }

  if (batchDragBox.x > realRect.x) {
    batchDragBox.width += batchDragBox.x - realRect.x;
    batchDragBox.x = realRect.x;
  }
  if (batchDragBox.x + batchDragBox.width < realRect.x + realRect.width) {
    batchDragBox.width += realRect.x + realRect.width - (batchDragBox.x + batchDragBox.width);
  }
  if (batchDragBox.y > realRect.y) {
    batchDragBox.height += batchDragBox.y - realRect.y;
    batchDragBox.y = realRect.y;
  }
  if (batchDragBox.y + batchDragBox.height < realRect.y + realRect.height) {
    batchDragBox.height += realRect.y + realRect.height - (batchDragBox.y + batchDragBox.height);
  }

  const batchDraggingArea = {
    x: realRect.x - batchDragBox.x,
    y: realRect.y - batchDragBox.y,
    width: draggingArea.width - batchDragBox.width,
    height: draggingArea.height - batchDragBox.height,
  };

  pos = convertToOriginalScale(pos, scale);

  let newX = Math.max(pos.x, batchDraggingArea.x + allMargin.x + realRect.width / 2);
  newX = Math.min(newX, batchDraggingArea.x + batchDraggingArea.width + allMargin.x + realRect.width / 2);
  let newY = Math.max(pos.y, batchDraggingArea.y + allMargin.y + realRect.height / 2);
  newY = Math.min(newY, batchDraggingArea.y + batchDraggingArea.height + allMargin.y + realRect.height / 2);
  return convertToRealScale({ x: newX, y: newY }, scale);
};

// reverse co-ords if users drag left/up
export const reverse = (r1, r2) => {
  let r1x = r1.x,
    r1y = r1.y,
    r2x = r2.x,
    r2y = r2.y,
    d;
  if (r1x > r2x) {
    d = Math.abs(r1x - r2x);
    r1x = r2x;
    r2x = r1x + d;
  }
  if (r1y > r2y) {
    d = Math.abs(r1y - r2y);
    r1y = r2y;
    r2y = r1y + d;
  }
  return { x1: r1x, y1: r1y, x2: r2x, y2: r2y }; // return the corrected rect.
};

//check if two rects intersect
export const checkIntersect = (coverRect, selectRect) => {
  const s1 = coverRect.getClientRect(); // use this to get bounding rect for shapes other than rectangles.
  const s2 = selectRect.getClientRect();

  // corners of shape 1
  const crLeft = s1.x;
  const crTop = s1.y;
  const crRight = s1.x + s1.width;
  const crBottom = s1.y + s1.height;

  // corners of shape 2
  const srLeft = s2.x;
  const srTop = s2.y;
  const srRight = s2.x + s2.width;
  const srBottom = s2.y + s2.height;

  // Simple overlapping rect collision test
  if (crRight < srLeft || srRight < crLeft || crBottom < srTop || srBottom < crTop) {
    return false;
  } else {
    return true;
  }
};

// get the real rect of current text element(may be rotated)
export const getTextRealRect = (elementData) => {
  const rotateResult = rotateText(
    [
      { x: elementData.x, y: elementData.y },
      { x: elementData.x + elementData.width, y: elementData.y },
      { x: elementData.x + elementData.width, y: elementData.y + elementData.height },
      { x: elementData.x, y: elementData.y + elementData.height },
    ],
    elementData.direction
  );

  return rotateResult.outlineRect;
};

export const getElementRealRectByDirection = (elementRect, direction) => {
  const originPos = { x: elementRect.x, y: elementRect.y };
  let realPos = originPos;

  if (direction === 90) realPos = { x: originPos.x - elementRect.height, y: realPos.y };
  else if (direction === 180) realPos = { x: originPos.x - elementRect.width, y: realPos.y - elementRect.height };
  else if (direction === 270) realPos = { x: originPos.x, y: realPos.y - elementRect.width };

  const realSize =
    direction === 90 || direction === 270
      ? { width: elementRect.height, height: elementRect.width }
      : { width: elementRect.width, height: elementRect.height };
  return { ...realPos, ...realSize };
};

export const getElementOriginPosition = (elementRect, realPos) => {
  const rotateResult = rotateText(
    [
      { x: elementRect.x, y: elementRect.y },
      { x: elementRect.x + elementRect.width, y: elementRect.y },
      { x: elementRect.x + elementRect.width, y: elementRect.y + elementRect.height },
      { x: elementRect.x, y: elementRect.y + elementRect.height },
    ],
    elementRect.direction
  );

  let xChange = realPos.x - rotateResult.outlineRect.x;
  let yChange = realPos.y - rotateResult.outlineRect.y;

  return {
    x: elementRect.x + xChange,
    y: elementRect.y + yChange,
    outlineRect: rotateResult.outlineRect,
    rotatedRectPoints: rotateResult.rotatedRectPoints,
  };
};

// get the real rect of the base image(auto-crop area if exists) base on the partial image(cropped or rotated)
// you can imagine putting the base image on the part, keeping align with the part itself
export const getBaseRealRectFromPart = (elementData, resizeRatio, canvasScale) => {
  // note: autoCropRect/cropRect has been converted to crop-form in Toolbar.Crop handler
  const partRealRect = getLogoRealRectForCrop(elementData, resizeRatio, canvasScale);
  const baseRealSize = {
    width: elementData.autoCropRect.width * resizeRatio * canvasScale,
    height: elementData.autoCropRect.height * resizeRatio * canvasScale,
  };

  return {
    x: partRealRect.x - elementData.cropRect.x * resizeRatio * canvasScale,
    y: partRealRect.y - elementData.cropRect.y * resizeRatio * canvasScale,
    width: baseRealSize.width,
    height: baseRealSize.height,
  };
};

// export const getPartFromBaseRealRect = (newBaseImageRect, elementData, resizeRatio, canvasScale) => {
//   const partRealRect = getImageRealRect(elementData, resizeRatio, canvasScale);

//   const originWidth = baseImageRect.width/( resizeRatio * canvasScale);
//   const originHeight = baseImageRect.height/( resizeRatio * canvasScale);

// };

// get the real rect of current logo element(cropped or rotated)
const getLogoRealRectForCrop = (elementData, resizeRatio, canvasScale) => {
  const partRealSize = {
    width: elementData.finalCropRect.width * resizeRatio * canvasScale,
    height: elementData.finalCropRect.height * resizeRatio * canvasScale,
  };

  const originPos = { x: elementData.x, y: elementData.y };
  let realPos = convertToRealScale({ x: originPos.x, y: originPos.y }, canvasScale);

  if (elementData.direction === 90) realPos = { x: realPos.x - partRealSize.width, y: realPos.y };
  else if (elementData.direction === 180)
    realPos = { x: realPos.x - partRealSize.width, y: realPos.y - partRealSize.height };
  else if (elementData.direction === 270) realPos = { x: realPos.x, y: realPos.y - partRealSize.height };

  return { ...realPos, ...partRealSize };
};

export const getEditingTextParams = (textElementData, resizeRatio, canvasScale, studioScale) => {
  const textSize = {
    width: textElementData.width * resizeRatio * canvasScale,
    height: textElementData.height * resizeRatio * canvasScale,
  };

  const fontSize = textElementData.fontSize * canvasScale * studioScale;
  return { size: textSize, fontSize };
};

export const getSortedElementList = function (textList, imageList) {
  let elementList = [];
  if (textList) {
    elementList = elementList.concat(textList);
  }
  if (imageList) {
    elementList = elementList.concat(imageList);
  }
  elementList.sort((e1, e2) => e1.data.zIndex - e2.data.zIndex);
  return elementList;
};

export const getImageElementMinSize = (imageData) => {
  return adjustImageSizeToFitContainer(
    { width: imageData.originalWidth, height: imageData.originalHeight },
    { width: 10, height: 10 }
  );
};

export const getImageElementMaxSize = (imageData) => {
  return { width: imageData.originalWidth, height: imageData.originalHeight };
};

export const checkSnapLine = ({ canvasData, draggingArea, elementRect, elementGuid }) => {
  const lines = [];
  const isText = canvasData.textList.some((text) => text.ui.guid === elementGuid);
  centerSnapLine(canvasData, draggingArea, elementRect, elementGuid, lines, isText);
  alignmentSnapLine(canvasData, elementRect, elementGuid, lines, isText);
  return lines;
};

const centerSnapLine = (canvasData, draggingArea, elementRect, elementGuid, lines, isText) => {
  let elementRealRect = {};
  if (isText) {
    elementRealRect = elementRect;
  } else {
    const element = findElement(canvasData, elementGuid);
    elementRealRect = getElementRealRectByDirection(elementRect, element.data.direction);
  }

  var elementCenterPoint = {
    x: elementRealRect.x + elementRealRect.width / 2,
    y: elementRealRect.y + elementRealRect.height / 2,
  };

  var draggingAreaCenterPoint = {
    x: draggingArea.x + draggingArea.width / 2,
    y: draggingArea.y + draggingArea.height / 2,
  };

  if (Math.abs(draggingAreaCenterPoint.x - elementCenterPoint.x) < CANVAS_SNAP_THRESHOLD) {
    lines.push({
      point1: { x: draggingAreaCenterPoint.x, y: 0 },
      point2: { x: draggingAreaCenterPoint.x, y: canvasData.extraMarginY * 2 + canvasData.fullHeight },
      adjust: { x: draggingAreaCenterPoint.x - elementCenterPoint.x, y: 0 },
    });
  }
  if (Math.abs(draggingAreaCenterPoint.y - elementCenterPoint.y) < CANVAS_SNAP_THRESHOLD) {
    lines.push({
      point1: { x: 0, y: draggingAreaCenterPoint.y },
      point2: { x: canvasData.extraMarginX + 2 + canvasData.fullWidth, y: draggingAreaCenterPoint.y },
      adjust: { x: 0, y: draggingAreaCenterPoint.y - elementCenterPoint.y },
    });
  }
};

const alignmentSnapLine = (canvasData, elementRect, elementGuid, lines, isText) => {
  const rectangles = [];
  canvasData.imageList.forEach((image) => {
    const imageRealRect = getElementRealRectByDirection(image.data, image.data.direction);
    rectangles.push({
      x: imageRealRect.x,
      y: imageRealRect.y,
      width: imageRealRect.width,
      height: imageRealRect.height,
      guid: image.ui.guid,
    });
  });
  canvasData.textList.forEach((text) => {
    const textRealRect = text.ui.outlineRect;
    rectangles.push({
      x: textRealRect.x,
      y: textRealRect.y,
      width: textRealRect.width,
      height: textRealRect.height,
      guid: text.ui.guid,
    });
  });

  let element = findElement(canvasData, elementGuid);
  let elementRealRect = {};
  if (isText) {
    elementRealRect = elementRect;
  } else {
    elementRealRect = getElementRealRectByDirection(elementRect, element.data.direction);
  }

  addAlignmentSnapLine(canvasData, rectangles, elementRealRect, element, lines, canvasSnapLineMode.Left);
  addAlignmentSnapLine(canvasData, rectangles, elementRealRect, element, lines, canvasSnapLineMode.Right);
  addAlignmentSnapLine(canvasData, rectangles, elementRealRect, element, lines, canvasSnapLineMode.Top);
  addAlignmentSnapLine(canvasData, rectangles, elementRealRect, element, lines, canvasSnapLineMode.Bottom);
  addAlignmentSnapLine(canvasData, rectangles, elementRealRect, element, lines, canvasSnapLineMode.CenterX);
  addAlignmentSnapLine(canvasData, rectangles, elementRealRect, element, lines, canvasSnapLineMode.CenterY);
  addAlignmentSnapLine(canvasData, rectangles, elementRealRect, element, lines, canvasSnapLineMode.NextLeft);
  addAlignmentSnapLine(canvasData, rectangles, elementRealRect, element, lines, canvasSnapLineMode.NextRight);
  addAlignmentSnapLine(canvasData, rectangles, elementRealRect, element, lines, canvasSnapLineMode.NextTop);
  addAlignmentSnapLine(canvasData, rectangles, elementRealRect, element, lines, canvasSnapLineMode.NextBottom);
};

const addAlignmentSnapLine = (canvasData, rectangles, elementRect, element, lines, snapLineMode) => {
  let alignPosition = null;
  const endPoints = [];
  const currentRectInfo =
    snapLineMode === canvasSnapLineMode.Left || snapLineMode === canvasSnapLineMode.NextLeft
      ? { alignPosition: elementRect.x, endPoints: [elementRect.y, elementRect.y + elementRect.height] }
      : snapLineMode === canvasSnapLineMode.Right || snapLineMode === canvasSnapLineMode.NextRight
      ? {
          alignPosition: elementRect.x + elementRect.width,
          endPoints: [elementRect.y, elementRect.y + elementRect.height],
        }
      : snapLineMode === canvasSnapLineMode.Top || snapLineMode === canvasSnapLineMode.NextTop
      ? { alignPosition: elementRect.y, endPoints: [elementRect.x, elementRect.x + elementRect.width] }
      : snapLineMode === canvasSnapLineMode.Bottom || snapLineMode === canvasSnapLineMode.NextBottom
      ? {
          alignPosition: elementRect.y + elementRect.height,
          endPoints: [elementRect.x, elementRect.x + elementRect.width],
        }
      : snapLineMode === canvasSnapLineMode.CenterX
      ? {
          alignPosition: elementRect.x + elementRect.width / 2,
          endPoints: [elementRect.y, elementRect.y + elementRect.height],
        }
      : snapLineMode === canvasSnapLineMode.CenterY
      ? {
          alignPosition: elementRect.y + elementRect.height / 2,
          endPoints: [elementRect.x, elementRect.x + elementRect.width],
        }
      : {};

  rectangles.forEach((rect) => {
    const rectInfo =
      snapLineMode === canvasSnapLineMode.Left || snapLineMode === canvasSnapLineMode.NextRight
        ? { alignPosition: rect.x, endPoints: [rect.y, rect.y + rect.height] }
        : snapLineMode === canvasSnapLineMode.Right || snapLineMode === canvasSnapLineMode.NextLeft
        ? { alignPosition: rect.x + rect.width, endPoints: [rect.y, rect.y + rect.height] }
        : snapLineMode === canvasSnapLineMode.Top || snapLineMode === canvasSnapLineMode.NextBottom
        ? { alignPosition: rect.y, endPoints: [rect.x, rect.x + rect.width] }
        : snapLineMode === canvasSnapLineMode.Bottom || snapLineMode === canvasSnapLineMode.NextTop
        ? { alignPosition: rect.y + rect.height, endPoints: [rect.x, rect.x + rect.width] }
        : snapLineMode === canvasSnapLineMode.CenterX
        ? { alignPosition: rect.x + rect.width / 2, endPoints: [rect.y, rect.y + rect.height] }
        : snapLineMode === canvasSnapLineMode.CenterY
        ? { alignPosition: rect.y + rect.height / 2, endPoints: [rect.x, rect.x + rect.width] }
        : {};

    if (
      rect.guid !== element.ui.guid &&
      Math.abs(rectInfo.alignPosition - currentRectInfo.alignPosition) < CANVAS_SNAP_THRESHOLD
    ) {
      // not add centerY lines for two text elements
      const rectElement = findElement(canvasData, rect.guid);
      if (
        snapLineMode !== canvasSnapLineMode.CenterY ||
        element.ui.fontFileName === undefined ||
        rectElement.ui.fontFileName === undefined
      ) {
        if (!alignPosition) alignPosition = rectInfo.alignPosition;
        endPoints.push(...rectInfo.endPoints);
      }
    }
  });

  if (endPoints.length > 0) {
    endPoints.push(...currentRectInfo.endPoints);

    const min = endPoints.sort(sortFunc)[0];
    const max = endPoints.sort(sortFunc).reverse()[0];
    if (
      snapLineMode === canvasSnapLineMode.Left ||
      snapLineMode === canvasSnapLineMode.NextLeft ||
      snapLineMode === canvasSnapLineMode.Right ||
      snapLineMode === canvasSnapLineMode.NextRight ||
      snapLineMode === canvasSnapLineMode.CenterX
    ) {
      lines.push({
        point1: { x: alignPosition, y: min },
        point2: { x: alignPosition, y: max },
        adjust: { x: alignPosition - currentRectInfo.alignPosition, y: 0 },
      });
    } else {
      lines.push({
        point1: { y: alignPosition, x: min },
        point2: { y: alignPosition, x: max },
        adjust: { y: alignPosition - currentRectInfo.alignPosition, x: 0 },
      });
    }
  }
};

const sortFunc = (a, b) => {
  return a - b;
};

const findElement = (canvasData, elementGuid) => {
  let element = canvasData.imageList.find((image) => image.ui.guid === elementGuid);
  if (element === undefined) element = canvasData.textList.find((text) => text.ui.guid === elementGuid);
  return element;
};

export const createMarginCover = () => {
  if (isMobileOnly) return;

  const step2Container = document.getElementById(domNodeId.step2Container);
  if (!step2Container) return;

  const marginLeft = parseInt(window.getComputedStyle(step2Container).marginLeft);
  const marginRight = parseInt(window.getComputedStyle(step2Container).marginRight);
  const clientRect = step2Container.getBoundingClientRect();

  let leftSideCover = document.getElementById(domNodeId.step2ContainerLeftSide);
  if (!leftSideCover) {
    leftSideCover = document.createElement('div');
    step2Container.append(leftSideCover);
  }
  leftSideCover.id = domNodeId.step2ContainerLeftSide;
  leftSideCover.style.position = 'absolute';
  leftSideCover.style.left = clientRect.left - marginLeft + 'px';
  leftSideCover.style.width = marginLeft + 'px';
  leftSideCover.style.top = clientRect.top + 'px';
  leftSideCover.style.height = clientRect.bottom - clientRect.top + 'px';

  let rightSideCover = document.getElementById(domNodeId.step2ContainerRightSide);
  if (!rightSideCover) {
    rightSideCover = document.createElement('div');
    step2Container.append(rightSideCover);
  }
  rightSideCover.id = domNodeId.step2ContainerRightSide;
  rightSideCover.style.position = 'absolute';
  rightSideCover.style.left = clientRect.right + 'px';
  rightSideCover.style.width = marginRight + 'px';
  rightSideCover.style.top = clientRect.top + 'px';
  rightSideCover.style.height = clientRect.bottom - clientRect.top + 'px';

  let bottomSideCover = document.getElementById(domNodeId.step2ContainerBottomSide);
  if (!bottomSideCover) {
    bottomSideCover = document.createElement('div');
    step2Container.append(bottomSideCover);
  }

  const appConfigurator = document.getElementById(domNodeId.appConfigurator);
  if (!appConfigurator) {
    return;
  }

  let appRect = appConfigurator.getBoundingClientRect();

  let screenHeight = window.innerHeight;

  var appHeight = parseInt(window.getComputedStyle(appConfigurator).height);

  bottomSideCover.id = domNodeId.step2ContainerBottomSide;
  bottomSideCover.style.position = 'absolute';
  bottomSideCover.style.left = appRect.left + 'px';
  bottomSideCover.style.width = appRect.width + 'px';
  bottomSideCover.style.top = clientRect.bottom + 'px';
  bottomSideCover.style.height = screenHeight - appHeight + 'px';
  bottomSideCover.style.zIndex = -1;
};

export const getColorForCanvasBorder = (color) => {
  const c = color.replace(/^#/, '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const invR = 255 - r;
  const invG = 255 - g;
  const invB = 255 - b;

  const calculateLuminance = (red, green, blue) => {
    return 0.2126 * (red / 255) + 0.7152 * (green / 255) + 0.0722 * (blue / 255);
  };
  const lOriginal = calculateLuminance(r, g, b);
  const lInverse = calculateLuminance(invR, invG, invB);
  const contrast = (Math.max(lOriginal, lInverse) + 0.05) / (Math.min(lOriginal, lInverse) + 0.05);

  if (contrast >= 3) {
    const toHex = (c) => c.toString(16).padStart(2, '0');
    return `#${toHex(invR)}${toHex(invG)}${toHex(invB)}`;
  } else {
    return lOriginal > 0.179 ? '#000000' : '#A6A6A6';
  }
};

export const getColorForGetStartedInCanvas = (color) => {
  const threshold = 15;
  const c = color.replace(/^#/, '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const invR = 255 - r;
  const invG = 255 - g;
  const invB = 255 - b;

  const calculateLuminance = (red, green, blue) => {
    return 0.2126 * (red / 255) + 0.7152 * (green / 255) + 0.0722 * (blue / 255);
  };
  const lOriginal = calculateLuminance(r, g, b);
  const lInverse = calculateLuminance(invR, invG, invB);
  const contrast = (Math.max(lOriginal, lInverse) + 0.05) / (Math.min(lOriginal, lInverse) + 0.05);

  if (contrast >= 3) {
    const toHex = (c) => c.toString(16).padStart(2, '0');
    return `#${toHex(invR)}${toHex(invG)}${toHex(invB)}`;
  } else if (Math.abs(r - g) < threshold && Math.abs(g - b) < threshold && Math.abs(r - b) < threshold) {
    return (r + g + b) / 3 > 200 ? '#000000' : '#A6A6A6';
  } else {
    return lOriginal > 0.355 ? '#000000' : '#A6A6A6';
  }
};

export const getSpecialColorForGetStartedInCanvas = (itemId, canvasKey, bomColor) => {
  let color = getStartedColorList.find(
    (c) => c.itemId === itemId && c.canvasKey === canvasKey && c.bomColor === bomColor
  );
  if (!color) color = getStartedColorList.find((c) => c.itemId === itemId && c.bomColor === bomColor);
  if (!color) color = getStartedColorList.find((c) => c.itemId === itemId);
  return color ? color.replacedColor : null;
};

export const getSpecialColorForCanvasBorder = (itemId, canvasKey, bomColor) => {
  let color = borderColorList.find((c) => c.itemId === itemId && c.canvasKey === canvasKey && c.bomColor === bomColor);
  if (!color) color = borderColorList.find((c) => c.itemId === itemId && c.bomColor === bomColor);
  if (!color) color = borderColorList.find((c) => c.itemId === itemId);
  return color ? color.replacedColor : null;
};
